/**
 * Favourites full-list page (DEV-50752 redesign).
 *
 * Engineers build up large favourite lists (100+ items) so a flat list
 * with a carousel isn't scannable — a plumber who stocks six sizes of
 * the same clip needs to distinguish them by MPN, not by supplier name.
 * This page groups favourites by their L2 category with sticky filter
 * chips at the top so an engineer can jump straight to "Clips" or
 * "Cable" without scrolling past everything else.
 *
 * Layout mirrors octopus-mock/app/favourites/page.tsx (Railway):
 *   1. Sticky nav-coloured header with back arrow + "Favourites (n)".
 *   2. Sticky chip row (only when ≥2 categories): "All (n)" plus one
 *      chip per category with its item count, plus a "+N more"
 *      dropdown when there are more than four categories to show.
 *   3. Filtered view adds a "Showing X of N favourites · Clear filter"
 *      strip and hides the per-section headers.
 *   4. Each L2 category renders as ONE card: rows sit inside a single
 *      `rounded-xl border` container, separated by inner dividers.
 *      Compact row = 44px thumbnail, product name with price and MPN
 *      underneath, then "Add to list" and the remove-heart. Supplier
 *      name is intentionally dropped — the mockup keys off part
 *      identity, not supplier.
 *
 * Products with no L2 category fall under the "Other" bucket, pinned
 * to the end of the list.
 *
 * DEV-51010 adds two tabs under the header, "All favourites" (default,
 * the view above) and "Lists" (named groups by job). The tab lives in the
 * URL (`?tab=lists`, replaced rather than pushed) so Back from a list
 * returns to the tab the engineer left. Each row gains an "Add to list"
 * button (see components/engineer/favourites/FavouriteRow).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, ChevronDown, Heart, Loader2, X } from 'lucide-react'
import { EngineerLayout } from '../../components/layout/EngineerLayout'
import { Button } from '../../components/radix'
import { FavouriteRow } from '../../components/engineer/favourites/FavouriteRow'
import { ListsPanel } from '../../components/engineer/favourites/ListsPanel'
import { useFavourites } from '../../auth/hooks/useFavourites'
import { heightVarRef } from '../../utils/heightVarRef'
import type { Favourite } from '../../types'

interface EngineerSession {
  postcode: string
  latitude: number
  longitude: number
}

// Bucket label for favourites whose product has no L2 category (or
// whose category tree is shallower than L2). Kept as a constant so the
// grouping code, the chip row, and the section header all agree.
const OTHER_LABEL = 'Other'

// Cap on how many category chips render inline before overflow spills
// into the "+N more" dropdown. Matches the mockup — a wider chip row
// wraps badly on mobile and pushes the list content out of the way.
const MAX_VISIBLE_CHIPS = 4

interface CategoryGroup {
  // Stable identity for grouping and filtering. Uses the L2 category
  // id as a string, or the `'other'` sentinel when the product has no
  // L2 category. Keying by id (not name) is necessary because the
  // MasterCategory constraint only enforces name uniqueness within a
  // parent — two L2 categories under different L1s can legally share
  // a name, and a real L2 could even be named literally "Other".
  key: string
  name: string
  items: Favourite[]
}

// Sentinel key for the "Other" bucket. Prefixed so it can never
// collide with a real numeric category id.
const OTHER_KEY = 'other'

type FavouritesTab = 'all' | 'lists'

const TAB_PARAM = 'tab'
const LISTS_TAB: FavouritesTab = 'lists'

const TABS: { id: FavouritesTab; label: string }[] = [
  { id: 'all', label: 'All favourites' },
  { id: 'lists', label: 'Lists' },
]

// The header and tabs stick together; the chip row sticks right under
// them, offset by their measured height.
const stickyHeaderRef = heightVarRef('--favourites-header-height')

export function FavouritesPage() {
  const navigate = useNavigate()
  const { tenantSlug } = useParams<{ tenantSlug: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const tab: FavouritesTab = searchParams.get(TAB_PARAM) === LISTS_TAB ? 'lists' : 'all'
  const [ready, setReady] = useState(false)

  const selectTab = (next: FavouritesTab) => {
    setSearchParams(
      (previous) => {
        const params = new URLSearchParams(previous)
        if (next === LISTS_TAB) params.set(TAB_PARAM, LISTS_TAB)
        else params.delete(TAB_PARAM)
        return params
      },
      { replace: true },
    )
  }

  useEffect(() => {
    const raw = sessionStorage.getItem('engineerSession')
    if (!raw) {
      navigate(`/${tenantSlug}/engineer`, { replace: true })
      return
    }
    try {
      JSON.parse(raw) as EngineerSession
      setReady(true)
    } catch {
      navigate(`/${tenantSlug}/engineer`, { replace: true })
    }
  }, [navigate, tenantSlug])

  const { favourites, isLoading } = useFavourites()

  // Which category is currently isolated. `null` = show all sections.
  // Keyed by the group's stable id (L2 category id, or `'other'` for
  // uncategorised) so that renaming an L2 category — or two L2s under
  // different L1s sharing a name — never breaks the filter identity.
  const [activeFilter, setActiveFilter] = useState<string | null>(null)

  // Group favourites by L2 category. Groups are sorted by item count
  // desc (with alphabetical tiebreak on display name) so the
  // categories an engineer uses most surface first; "Other" is
  // always last.
  const groups = useMemo<CategoryGroup[]>(() => {
    const grouped = new Map<string, CategoryGroup>()
    for (const fav of favourites) {
      const l2Id = fav.listing?.categoryL2Id
      const key = l2Id != null ? String(l2Id) : OTHER_KEY
      const name = fav.listing?.categoryL2Name || OTHER_LABEL
      const existing = grouped.get(key)
      if (existing) {
        existing.items.push(fav)
      } else {
        grouped.set(key, { key, name, items: [fav] })
      }
    }
    return Array.from(grouped.values()).sort((a, b) => {
      if (a.key === OTHER_KEY) return 1
      if (b.key === OTHER_KEY) return -1
      if (b.items.length !== a.items.length) return b.items.length - a.items.length
      return a.name.localeCompare(b.name)
    })
  }, [favourites])

  const totalCount = favourites.length
  const hasMultipleCategories = groups.length > 1

  // AC #9 — un-favouriting the last item in the active bucket clears
  // the filter, otherwise the engineer is stuck looking at an empty
  // section with no chip to tap back to.
  useEffect(() => {
    if (
      activeFilter !== null &&
      !groups.some((g) => g.key === activeFilter && g.items.length > 0)
    ) {
      setActiveFilter(null)
    }
  }, [groups, activeFilter])

  const visibleChips = useMemo(
    () => groups.slice(0, MAX_VISIBLE_CHIPS),
    [groups],
  )
  const overflowChips = useMemo(
    () => groups.slice(MAX_VISIBLE_CHIPS),
    [groups],
  )

  const filteredGroups = useMemo(() => {
    if (activeFilter === null) return groups
    return groups.filter((g) => g.key === activeFilter)
  }, [groups, activeFilter])

  const filteredCount = filteredGroups.reduce(
    (sum, g) => sum + g.items.length,
    0,
  )

  const handleChipClick = useCallback((key: string) => {
    // Tapping the active chip clears the filter (AC #5).
    setActiveFilter((prev) => (prev === key ? null : key))
  }, [])

  const clearFilter = useCallback(() => setActiveFilter(null), [])

  if (!ready) return null

  return (
    // `hideHeader` suppresses the shared EngineerHeader (postcode pill
    // + weekly-budget gauge). This page has its own sticky header with
    // the back arrow and title — stacking the two dark bars looked bad
    // and doesn't match the mockup, which shows one header only.
    <EngineerLayout hideHeader>
      {/* Sticky header + tabs (AC #10) — pinned together at the top. */}
      <div ref={stickyHeaderRef} className="sticky top-0 z-20">
        <div className="bg-nav text-nav border-b border-nav-divider">
          <div className="max-w-3xl mx-auto px-4 py-3">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="p-2 -ml-2 hover:bg-nav-hover rounded-xl transition-colors"
                aria-label="Back"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="font-bold">Favourites</h1>
              {totalCount > 0 && (
                <span className="text-sm opacity-60">({totalCount})</span>
              )}
            </div>
          </div>
        </div>
        <FavouritesTabs tab={tab} onChange={selectTab} />
      </div>

      {tab === 'lists' && (
        <main className="bg-gray-50 min-h-screen">
          <div className="max-w-3xl mx-auto px-4 py-5 pb-24">
            <div id="favourites-panel-lists" role="tabpanel" aria-labelledby="favourites-tab-lists">
              <ListsPanel />
            </div>
          </div>
        </main>
      )}

      {tab === 'all' && (
        <>
        {/* Sticky chip row directly under the header (AC #10). Hidden
         * with only one category — there's nothing to filter to. */}
        {totalCount > 0 && hasMultipleCategories && (
          <div className="sticky top-[var(--favourites-header-height,0px)] z-10 border-b border-gray-200 bg-gray-50/80 backdrop-blur-sm">
            <div className="max-w-3xl mx-auto px-4 py-2.5">
              <ChipRow
                visibleChips={visibleChips}
                overflowChips={overflowChips}
                totalCount={totalCount}
                activeFilter={activeFilter}
                onSelect={handleChipClick}
                onClear={clearFilter}
              />
            </div>
          </div>
        )}

        <main className="bg-gray-50 min-h-screen">
          <div
            id="favourites-panel-all"
            role="tabpanel"
            aria-labelledby="favourites-tab-all"
            className="max-w-3xl mx-auto px-4 py-5 pb-24"
          >
            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : totalCount === 0 ? (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-6">
                  <Heart className="w-10 h-10 text-brand-primary" />
                </div>
                <h2 className="text-gray-900 text-xl font-bold mb-3 text-center">
                  No favourites yet
                </h2>
                <p className="text-gray-500 text-base text-center mb-6 max-w-sm">
                  Tap the heart icon on any product while searching to save it
                  here — it&rsquo;ll show your best supplier for your current
                  postcode.
                </p>
                <Button onClick={() => navigate(`/${tenantSlug}/engineer/search`)}>
                  Search products
                </Button>
              </div>
            ) : (
              <>
                {activeFilter !== null && (
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-gray-500 text-xs">
                      Showing {filteredCount} of {totalCount} favourite
                      {totalCount === 1 ? '' : 's'}
                    </span>
                    <button
                      onClick={clearFilter}
                      className="text-xs font-medium text-brand-primary flex items-center gap-1 hover:opacity-80"
                    >
                      Clear filter
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}

                <div className="space-y-5">
                  {filteredGroups.map((group) => (
                    <section key={group.key}>
                      {/* Section header hidden while filtered — the
                       * filter strip above already tells the engineer
                       * what they're looking at. */}
                      {activeFilter === null && (
                        <div className="mb-2 px-1">
                          <h3 className="text-gray-900 font-bold text-sm">
                            {group.name}{' '}
                            <span className="text-gray-400 font-medium">
                              &middot; {group.items.length}
                            </span>
                          </h3>
                        </div>
                      )}

                      {/* The whole category is a single card — rows sit
                       * inside, separated by inner dividers. */}
                      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden divide-y divide-gray-100">
                        {group.items.map((fav) => (
                          <FavouriteRow key={fav.id} favourite={fav} />
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              </>
            )}
          </div>
        </main>
        </>
      )}
    </EngineerLayout>
  )
}

/**
 * "All favourites" / "Lists" tab bar. Arrow keys switch tabs (roving
 * tabindex, WAI-ARIA tabs pattern); the selected tab has a brand underline.
 */
function FavouritesTabs({
  tab,
  onChange,
}: {
  tab: FavouritesTab
  onChange: (tab: FavouritesTab) => void
}) {
  const tabRefs = useRef<Record<FavouritesTab, HTMLButtonElement | null>>({ all: null, lists: null })

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
    event.preventDefault()
    const next: FavouritesTab = tab === 'all' ? 'lists' : 'all'
    onChange(next)
    tabRefs.current[next]?.focus()
  }

  return (
    <div className="border-b border-gray-200 bg-white">
      <div
        role="tablist"
        aria-label="Favourites"
        className="max-w-3xl mx-auto flex px-4"
        onKeyDown={onKeyDown}
      >
        {TABS.map(({ id, label }) => {
          const selected = tab === id
          return (
            <button
              key={id}
              ref={(el) => {
                tabRefs.current[id] = el
              }}
              id={`favourites-tab-${id}`}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls={selected ? `favourites-panel-${id}` : undefined}
              tabIndex={selected ? 0 : -1}
              onClick={() => onChange(id)}
              className={
                'relative h-12 flex-1 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-primary ' +
                (selected ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700')
              }
            >
              {label}
              {selected && (
                <span
                  className="absolute inset-x-3 bottom-0 h-0.5 rounded-full bg-brand-primary"
                  aria-hidden="true"
                />
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

/**
 * Horizontal chip row with overflow dropdown. Renders "All (N)" first,
 * then up to `MAX_VISIBLE_CHIPS` category chips, then a "+N more"
 * dropdown for any remainder. Wraps rather than scrolls so nothing
 * hides behind a horizontal scrollbar on mobile.
 */
function ChipRow({
  visibleChips,
  overflowChips,
  totalCount,
  activeFilter,
  onSelect,
  onClear,
}: {
  visibleChips: CategoryGroup[]
  overflowChips: CategoryGroup[]
  totalCount: number
  activeFilter: string | null
  onSelect: (key: string) => void
  onClear: () => void
}) {
  const [showOverflow, setShowOverflow] = useState(false)
  const overflowRef = useRef<HTMLDivElement | null>(null)

  // Close the overflow menu when clicking outside so it doesn't hang
  // around after the engineer taps back to the main page.
  useEffect(() => {
    if (!showOverflow) return
    function handleDocumentClick(event: MouseEvent) {
      if (!overflowRef.current) return
      if (!overflowRef.current.contains(event.target as Node)) {
        setShowOverflow(false)
      }
    }
    document.addEventListener('mousedown', handleDocumentClick)
    return () => document.removeEventListener('mousedown', handleDocumentClick)
  }, [showOverflow])

  const overflowActive = overflowChips.some((g) => g.key === activeFilter)

  return (
    <div className="flex gap-2 items-center flex-wrap">
      <Chip
        label={`All (${totalCount})`}
        active={activeFilter === null}
        onClick={onClear}
      />
      {visibleChips.map((group) => (
        <Chip
          key={group.key}
          label={`${group.name} (${group.items.length})`}
          active={activeFilter === group.key}
          onClick={() => onSelect(group.key)}
        />
      ))}
      {overflowChips.length > 0 && (
        <div className="relative" ref={overflowRef}>
          <button
            onClick={() => setShowOverflow((prev) => !prev)}
            className={
              'flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap flex items-center gap-1 ' +
              (overflowActive
                ? 'bg-brand-primary text-white'
                : 'bg-gray-100 text-gray-700')
            }
            aria-haspopup="menu"
            aria-expanded={showOverflow}
          >
            +{overflowChips.length} more
            <ChevronDown className="w-3 h-3" />
          </button>
          {showOverflow && (
            <div
              role="menu"
              className="absolute top-full mt-1 right-0 z-30 bg-white rounded-xl border border-gray-200 shadow-lg py-1 min-w-[180px]"
            >
              {overflowChips.map((group) => (
                <button
                  key={group.key}
                  role="menuitem"
                  onClick={() => {
                    onSelect(group.key)
                    setShowOverflow(false)
                  }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between"
                >
                  <span
                    className={
                      activeFilter === group.key
                        ? 'font-semibold text-gray-900'
                        : 'text-gray-700'
                    }
                  >
                    {group.name}
                  </span>
                  <span className="text-xs text-gray-400">
                    {group.items.length}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function Chip({
  label,
  active,
  onClick,
}: {
  label: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={
        'flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ' +
        (active ? 'bg-brand-primary text-white' : 'bg-gray-100 text-gray-700')
      }
      aria-pressed={active}
    >
      {label}
    </button>
  )
}
